import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheckIcon, FlagIcon, WaveformIcon } from "@phosphor-icons/react";

/**
 * Moderator dashboard — live overview of every active stream + recent
 * flags and the mod's own action history. Admins can also visit.
 */
export default function ModDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState({ live_streams: [], open_flags_total: 0 });
  const [flags, setFlags] = useState([]);
  const [mine, setMine] = useState([]);

  const isStaff = user && (user.role === "moderator" || user.role === "admin");

  useEffect(() => {
    if (loading) return;
    if (!isStaff) {
      navigate("/");
      return;
    }
    refresh();
    const t = setInterval(refresh, 20000); // poll every 20s
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user, loading]);

  const refresh = async () => {
    try {
      const [ov, fl, ma] = await Promise.all([
        api.get("/mod/overview"),
        api.get("/mod/flags", { params: { status: "open" } }),
        api.get("/mod/actions/mine", { params: { limit: 15 } }),
      ]);
      setOverview(ov.data);
      setFlags(fl.data.items || []);
      setMine(ma.data.items || []);
    } catch (e) {
      console.error(e);
      toast.error(apiError(e));
    }
  };

  if (loading || !isStaff) return null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 py-10" data-testid="mod-dashboard">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#1D78AB] mb-2 flex items-center gap-2">
              <ShieldCheckIcon size={14} weight="fill" /> MODERATOR DASHBOARD
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl leading-none">
              Keep the community safe.
            </h1>
            <p className="text-zinc-600 mt-2 max-w-2xl">
              Jump into any live stream to watch chat, delete messages, mute users, or flag
              the stream for admin review. Every action is logged.
            </p>
          </div>
          <Button variant="outline" onClick={refresh} data-testid="mod-refresh-btn">
            Refresh
          </Button>
        </div>

        {/* KPI strip */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <KpiCard label="Live streams" value={overview.live_streams.length} testid="mod-kpi-live" />
          <KpiCard label="Open flags" value={overview.open_flags_total} accent={overview.open_flags_total ? "text-amber-600" : ""} testid="mod-kpi-flags" />
          <KpiCard label="Active mutes" value={overview.live_streams.reduce((s, x) => s + (x.active_mutes || 0), 0)} testid="mod-kpi-mutes" />
        </div>

        {/* Live streams list */}
        <section className="mb-10">
          <h2 className="font-display font-bold text-lg mb-3">Live right now</h2>
          {overview.live_streams.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-2xl p-10 text-center text-zinc-500">
              Nothing's live. Breathe.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {overview.live_streams.map((s) => (
                <Link
                  key={s.id}
                  to={`/stream/${s.id}`}
                  className="group border border-zinc-200 hover:border-[#1D78AB] rounded-2xl p-4 bg-white transition"
                  data-testid={`mod-stream-card-${s.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="pill pill-live text-[10px] uppercase tracking-widest">
                      <span className="live-dot inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-1" />
                      LIVE
                    </span>
                    <span className="pill pill-dark text-[10px] uppercase tracking-widest">{s.segment}</span>
                    {s.open_flags > 0 && (
                      <span className="pill bg-amber-100 text-amber-800 border border-amber-300 text-[10px] uppercase tracking-widest">
                        <FlagIcon size={10} weight="fill" className="inline mr-0.5" />
                        {s.open_flags} flag{s.open_flags > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-sm leading-tight line-clamp-2 group-hover:text-[#1D78AB]">
                    {s.title || "Untitled stream"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">{s.seller_name}</div>
                  <div className="flex items-center gap-3 text-[11px] text-zinc-500 font-mono mt-3">
                    <span className="inline-flex items-center gap-1">
                      <WaveformIcon size={12} /> {s.viewer_count || 0} watching
                    </span>
                    {s.peak_viewers > 0 && <span>peak {s.peak_viewers}</span>}
                    {s.active_mutes > 0 && <span className="text-red-600">· {s.active_mutes} muted</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Open flags */}
        <section className="mb-10">
          <h2 className="font-display font-bold text-lg mb-3">Open flags</h2>
          {flags.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-2xl p-6 text-sm text-zinc-500 text-center">
              No flags waiting for review.
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-2xl overflow-hidden divide-y divide-zinc-200">
              {flags.map((f) => (
                <div key={f.id} className="p-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`mod-flag-row-${f.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-2">
                      <span className="pill pill-dark text-[10px] uppercase tracking-widest">{f.category}</span>
                      {f.stream_title || f.stream_id}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      by {f.flagged_by_name} · {new Date(f.created_at).toLocaleString()}
                    </div>
                    <p className="text-sm text-zinc-700 mt-2 bg-zinc-50 rounded-lg p-2 border border-zinc-200">
                      {f.reason}
                    </p>
                  </div>
                  <Link to={`/stream/${f.stream_id}`} className="text-xs text-[#1D78AB] underline shrink-0">
                    Open stream →
                  </Link>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* My recent actions */}
        <section className="mb-16">
          <h2 className="font-display font-bold text-lg mb-3">My recent actions</h2>
          {mine.length === 0 ? (
            <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-2xl p-6 text-center">
              You haven't taken any moderation actions yet.
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-2xl overflow-hidden divide-y divide-zinc-200">
              {mine.map((a) => (
                <div key={a.id} className="p-3 flex items-center justify-between gap-2 flex-wrap text-sm">
                  <div>
                    <span className="font-mono text-xs text-[#1D78AB]">{a.action}</span>
                    <span className="text-zinc-500"> · </span>
                    <span className="text-xs text-zinc-600">{a.target_type} {a.target_id?.slice(-10)}</span>
                    {a.reason && <span className="text-zinc-500"> — {a.reason}</span>}
                  </div>
                  <div className="text-[11px] text-zinc-400 font-mono">
                    {new Date(a.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function KpiCard({ label, value, accent = "", testid }) {
  return (
    <div className="border border-zinc-200 rounded-2xl p-4 bg-white" data-testid={testid}>
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display font-black text-3xl mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
